import { apiClient } from './client';

// ── Tipos ─────────────────────────────────────────────────────────────────────

export type FolioStatus = 'OPEN' | 'IN_TRANSIT' | 'DELIVERED' | 'CLOSED';

export type TraceabilityEventType =
  | 'FIELD_OP' | 'HARVEST' | 'PACKING'
  | 'CONTAINER' | 'CUSTOMS' | 'EXPORT' | 'COOLING';

export interface TraceabilityFolio {
  folioId:               string;
  tenantId:              string;
  loteId:                string;
  product:               string;
  variety:               string;
  estimatedQuantityKg:   number;
  status:                FolioStatus;
  createdAt:             string;
  updatedAt:             string;
  destination?:          string;
  buyer?:                string;
  certifications?:       string[];
}

export interface TraceabilityEvent {
  folioId:           string;
  eventId:           string;
  type:              TraceabilityEventType;
  occurredAt:        string;
  description:       string;
  loteId:            string;
  linkedActivityId?: string;
  quantityKg?:       number;
  temperature?:      number;
  location?:         string;
  operator?:         string;
  metadata?:         Record<string, unknown>;
  // KDE de enfriado (FSMA 204)
  coolingLocation?:    string;
  coolingDate?:        string;
  recipientLocation?:  string;
  referenceDocType?:   string;
  referenceDocNumber?: string;
  product?:            string;
  variety?:            string;
}

export interface CreateFolioInput {
  loteId:               string;
  product:              string;
  variety:              string;
  estimatedQuantityKg:  number;
  destination?:         string;
  buyer?:               string;
  certifications?:      string[];
}

export interface AddEventInput {
  loteId:            string;
  type:              TraceabilityEventType;
  description:       string;
  linkedActivityId?: string;
  quantityKg?:       number;
  temperature?:      number;
  location?:         string;
  operator?:         string;
  metadata?:         Record<string, unknown>;
  // KDE de enfriado (FSMA 204)
  coolingLocation?:    string;
  coolingDate?:        string;
  recipientLocation?:  string;
  referenceDocType?:   string;
  referenceDocNumber?: string;
  product?:            string;
  variety?:            string;
}

// ── Queries ───────────────────────────────────────────────────────────────────

export const fetchFolios = async (
  loteId: string,
  status?: FolioStatus,
): Promise<{ folios: TraceabilityFolio[]; count: number }> =>
  (await apiClient.get('/api/traceability/folios', { params: { loteId, status } })).data;

export const fetchFolio = async (
  folioId: string,
): Promise<{ folio: TraceabilityFolio; events: TraceabilityEvent[] }> =>
  (await apiClient.get(`/api/traceability/folios/${folioId}`)).data;

// ── Mutaciones ────────────────────────────────────────────────────────────────

export const createFolio = async (
  input: CreateFolioInput,
): Promise<TraceabilityFolio> =>
  (await apiClient.post('/api/traceability/folios', input)).data;

export const addFolioEvent = async (
  folioId: string,
  input: AddEventInput,
): Promise<TraceabilityEvent> =>
  (await apiClient.post(`/api/traceability/folios/${folioId}/events`, input)).data;

// ── Plan de trazabilidad (21 CFR 1.1315) ─────────────────────────────────────

export interface TraceabilityPlan {
  tenantId:                     string;
  recordsLocation:              string;
  recordsFormat:                string;
  foodIdentificationProcedure?: string;
  tlcAssignmentProcedure:       string;
  contactName:                  string;
  contactPhone:                 string;
  contactEmail?:                string;
  coveredFoodsNote?:            string;
  updatedAt:                    string;
  updatedBy:                    string;
  version:                      number;
}

export type TraceabilityPlanInput = Omit<TraceabilityPlan, 'tenantId' | 'updatedAt' | 'updatedBy' | 'version'>;

export const fetchTraceabilityPlan = async (): Promise<TraceabilityPlan | null> =>
  (await apiClient.get('/api/traceability/plan')).data.plan;

export const saveTraceabilityPlan = async (input: TraceabilityPlanInput): Promise<TraceabilityPlan> =>
  (await apiClient.put('/api/traceability/plan', input)).data;

export const exportTraceabilityPlan = async (format: 'xlsx' | 'geojson'): Promise<{ fileName: string; fileBase64: string }> =>
  (await apiClient.get('/api/traceability/plan/export', { params: { format } })).data;

// ── Hoja FSMA, rastreo y simulacro (fase D) ──────────────────────────────────

export interface FsmaExportParams {
  tlc?: string;
  from?: string;
  to?: string;
  format: 'xlsx' | 'csv';
}

export const exportFsmaSheet = async (p: FsmaExportParams): Promise<{ fileName: string; contentType: string; fileBase64: string; truncated?: boolean }> =>
  (await apiClient.get('/api/traceability/fsma/export', { params: p })).data;

export interface TraceResult {
  tlc: string;
  direction: 'forward' | 'backward';
  lotes: string[];
  folios: string[];
  shipments: { shipmentId: string; buyer?: string; shipDate?: string; kg?: number }[];
  unmatchedKg: number;
}

export const traceFsmaTlc = async (tlc: string, direction: 'forward' | 'backward'): Promise<TraceResult> =>
  (await apiClient.get(`/api/traceability/fsma/trace/${encodeURIComponent(tlc)}`, { params: { direction } })).data;

export interface RecallDrill {
  drillId: string;
  tlc: string;
  startedAt: string;
  finishedAt: string;
  elapsedMinutes: number;
  harvestedKg: number;
  shippedKg: number;
  unaccountedKg: number;
  unaccountedPct: number;
  passed: boolean | null;
  notes?: string;
  createdAt: string;
}

export interface RunRecallDrillInput {
  tlc: string;
  startedAt: string;
  finishedAt: string;
  mermaKg?: number;
  notes?: string;
}

export const listRecallDrills = async (): Promise<RecallDrill[]> =>
  (await apiClient.get('/api/traceability/recall-drills')).data.items;

export const runRecallDrill = async (input: RunRecallDrillInput): Promise<RecallDrill> =>
  (await apiClient.post('/api/traceability/recall-drills', input)).data.drill;
